/**
 * <sticky-buy-bar> — fixed bottom bar that slides in once the main Add to cart button
 * leaves the viewport (scrolled past it, not before it).
 * - variantChange (product-info.js): updates variant title, price, thumb and availability.
 * - ATC submits the main product form (product-form.js does the cart request).
 *   No variant / sold out → scrolls to the size picker or shows "Notify me".
 * - Hidden for 1s when the cart drawer opens after an add.
 */
if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.submit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.form = document.getElementById(`product-form-${this.sectionId}`);
        this.addButton = this.querySelector('[data-sticky-add]');
        this.notifyButton = this.querySelector('[data-sticky-notify]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.variantEl = this.querySelector('[data-sticky-variant]');
        this.thumb = this.querySelector('[data-sticky-thumb] img');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.addButton?.addEventListener('click', () => this.onAdd());

        if (this.submit && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.toggle(scrolledPast);
            },
            { threshold: 0 }
          );
          this.observer.observe(this.submit);
        }

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId !== this.sectionId) return;
            this.update(data);
          });
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            if (document.querySelector('cart-drawer.active')) this.suppress(1000);
          });
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.unsubscribeVariant?.();
        this.unsubscribeCart?.();
        clearTimeout(this.suppressTimer);
      }

      toggle(show) {
        this.shouldShow = show;
        this.classList.toggle('is-visible', Boolean(show) && !this.suppressed);
      }

      suppress(ms) {
        this.suppressed = true;
        this.classList.remove('is-visible');
        clearTimeout(this.suppressTimer);
        this.suppressTimer = setTimeout(() => {
          this.suppressed = false;
          this.toggle(this.shouldShow);
        }, ms);
      }

      update({ variant, html }) {
        const available = Boolean(variant && variant.available);
        const variantTitle = variant && variant.title !== 'Default Title' ? variant.title : '';

        if (this.variantEl) this.variantEl.textContent = variantTitle;

        const price = html?.getElementById(`price-${this.sectionId}`);
        if (price && this.priceEl) this.priceEl.innerHTML = price.innerHTML;

        const src = variant?.featured_media?.preview_image?.src || variant?.featured_image?.src;
        if (src && this.thumb) {
          try {
            const url = new URL(src, window.location.origin);
            url.searchParams.set('width', '96');
            this.thumb.removeAttribute('srcset');
            this.thumb.src = url.toString();
          } catch (e) {
            /* keep the current thumb */
          }
        }

        this.addButton?.classList.toggle('hidden', !available);
        if (this.notifyButton) {
          this.notifyButton.classList.toggle('hidden', available || !variant);
          if (variant) {
            this.notifyButton.dataset.variantId = variant.id;
            if (variantTitle) this.notifyButton.dataset.variantTitle = variantTitle;
            else delete this.notifyButton.dataset.variantTitle;
          }
        }
      }

      onAdd() {
        const idInput = this.form?.querySelector('[name="id"]');
        const canSubmit = this.form && idInput?.value && this.submit && !this.submit.hasAttribute('disabled');
        if (!canSubmit) {
          this.scrollToPicker();
          return;
        }
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.submit);
        else this.submit.click();
      }

      scrollToPicker() {
        const picker = document.getElementById(`variant-selects-${this.sectionId}`);
        const target = picker || this.submit;
        if (!target) return;
        target.scrollIntoView({ block: 'center', behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
        target.querySelector?.('input:checked, input')?.focus({ preventScroll: true });
      }
    }
  );
}
